import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import css from './Table.module.scss';
import {TableProvider, useTableContext} from './TableContext';

const TableInner = ({children, className, cellAlign, compact, style, columnIds, fixedColumnWidths, tableRef}) => {
  const {columnWidths} = useTableContext();

  const hasColumnIds = columnIds && columnIds.length > 0;
  const resolveWidth = (id) => {
    if (fixedColumnWidths && fixedColumnWidths[id] !== undefined) {
      return fixedColumnWidths[id];
    }
    return columnWidths[id];
  };
  const allMeasured = hasColumnIds && columnIds.every(id => resolveWidth(id) !== undefined);

  return (
    <table
      ref={tableRef}
      className={`${css.table} ${css[`align-${cellAlign}`] || ''} ${compact ? css.compact : ''} ${hasColumnIds ? css.tableResizable : ''} ${allMeasured ? css.tableFixed : ''} ${className}`.trim()}
      style={style}
    >
      {allMeasured &&
        <colgroup>
          {columnIds.map(id => (
            <col key={id} style={{width: `${resolveWidth(id)}px`}} />
          ))}
        </colgroup>
      }
      {children}
    </table>
  );
};

const Table = ({
  children,
  className = '',
  cellAlign = 'left',
  style,
  scrollable = false,
  compact = false,
  storageKey,
  columnIds,
  fixedColumnWidths,
}) => {
  const tableRef = useRef(null);

  const tableElement = (
    <TableProvider
      cellAlign={cellAlign}
      storageKey={storageKey}
      columnIds={columnIds}
      fixedColumnWidths={fixedColumnWidths}
      tableRef={tableRef}
    >
      <TableInner
        className={className}
        cellAlign={cellAlign}
        compact={compact}
        style={style}
        columnIds={columnIds}
        fixedColumnWidths={fixedColumnWidths}
        tableRef={tableRef}
      >
        {children}
      </TableInner>
    </TableProvider>
  );

  if (scrollable) {
    return (
      <div className={css.scrollableTableWrapper}>
        {tableElement}
      </div>
    );
  }

  return tableElement;
};

Table.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  cellAlign: PropTypes.oneOf(['left', 'center', 'right']),
  style: PropTypes.object,
  scrollable: PropTypes.bool,
  compact: PropTypes.bool,
  storageKey: PropTypes.string,
  columnIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fixedColumnWidths: PropTypes.object,
};

TableInner.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  cellAlign: PropTypes.oneOf(['left', 'center', 'right']),
  compact: PropTypes.bool,
  style: PropTypes.object,
  columnIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fixedColumnWidths: PropTypes.object,
  tableRef: PropTypes.shape({current: PropTypes.any}),
};

export default Table;
